/**
 * D.2.b — thin Azure AI Search REST client.
 *
 * Pure (no React); the SPA component injects fetch in tests. Production
 * uses global `fetch`. We deliberately keep this surface narrow — one
 * `searchCorpus` call — so the @azure/search-documents SDK isn't a SPA
 * dependency. SDK would balloon the bundle ~80KB for one POST.
 *
 * Memory: feedback_graph_retry_policy — Azure AI Search calls don't go
 * through the lib/graph retry wrappers (those are Microsoft Graph-only).
 * If we hit throttling later, add an Azure-specific retry policy here.
 *
 * Phase E adds vector + hybrid + semantic modes. The body shape is a
 * superset of Phase D — same defaults when called with no `mode`.
 */

/** Search modes. Phase E. */
export type SearchMode = 'keyword' | 'vector' | 'hybrid';

export interface CorpusHit {
  /** Azure AI Search document key — `${slug}_${docId}_${base64urlVersion}`. */
  key: string;
  /** Convex `documents._id`. Used by the SPA's open-in-Explorer action. */
  documentId: string;
  /** Display title from the indexed doc. */
  title: string;
  /** Per-portal scope. Lets the SPA badge results 'live' vs 'demo'. */
  clientSlug: string;
  /** Azure AI Search-supplied snippet/highlight if present. */
  snippet: string;
  /** Search-engine relevance score (debug surface only). */
  score: number;
  /** Last-modified ISO timestamp. */
  lastModifiedDateTime: string;
}

export interface SearchCorpusInput {
  endpoint: string;
  indexName: string;
  queryKey: string;
  /** Text query. Required for 'keyword' and 'hybrid'; allowed for 'vector' (ignored). */
  query: string;
  /** OData $filter expression from D.2.a issueForPortal. */
  filterClause: string;
  /** Page size; defaults to 25. */
  top?: number;
  /** Optional fetch override for tests. */
  fetchImpl?: typeof fetch;

  // ── Phase E additions ─────────────────────────────────────────────
  /**
   * 'keyword' (default) — BM25 only, current Phase D behavior.
   * 'vector' — k-NN against the `embedding` field. `vector` REQUIRED.
   * 'hybrid' — BM25 + k-NN. Both `query` and `vector` REQUIRED. Azure
   *   merges + RRF-fuses the two result sets natively.
   */
  mode?: SearchMode;
  /**
   * 1536-dim query embedding. Computed server-side via
   * `searchToken.embedQuery` so the OpenAI key never reaches the SPA.
   * Required for 'vector' and 'hybrid' modes.
   */
  vector?: number[];
  /**
   * When true, enable Azure's L2 semantic ranker on top of the result set
   * (works for any mode; particularly effective with 'hybrid'). Requires the
   * index to declare a semantic configuration (we name ours `repancy-semantic`).
   * The semantic ranker re-orders the top 50 BM25/vector hits using a
   * Microsoft-trained transformer and adds `@search.captions` highlights.
   * Costs extra per query — keep off for autocomplete-style flows.
   */
  semantic?: boolean;
}

export interface SearchCorpusResult {
  hits: CorpusHit[];
  totalCount: number | null;
}

/**
 * Azure Search API version used by all SPA queries. Bumped from
 * `2023-11-01` to `2024-07-01` in Phase E because vector + semantic
 * features require the newer surface (and the index was provisioned
 * with that version).
 */
const SEARCH_API_VERSION = '2024-07-01';

/** Semantic config name embedded into the index by buildSearchIndexDefinition. */
const SEMANTIC_CONFIG_NAME = 'repancy-semantic';

export async function searchCorpus(
  input: SearchCorpusInput,
): Promise<SearchCorpusResult> {
  const mode: SearchMode = input.mode ?? 'keyword';
  if ((mode === 'vector' || mode === 'hybrid') && !input.vector) {
    throw new Error(`searchCorpus: mode "${mode}" requires \`vector\``);
  }
  if (mode === 'keyword' && input.query.trim().length === 0) {
    throw new Error('searchCorpus: mode "keyword" requires a non-empty query');
  }

  const f = input.fetchImpl ?? fetch;
  const url =
    `${input.endpoint.replace(/\/+$/, '')}` +
    `/indexes/${encodeURIComponent(input.indexName)}/docs/search` +
    `?api-version=${SEARCH_API_VERSION}`;

  // Build body. Azure's REST surface unifies keyword + vector + semantic
  // under one POST — fields are additive. Mode just toggles which subset
  // we include.
  const top = input.top ?? 25;
  // 'keyword' sends only the text search; 'vector' sends only the vector
  // (empty `search` so BM25 contributes nothing — the response still goes
  // through ACL filter); 'hybrid' sends both.
  const body: Record<string, unknown> = {
    top,
    count: true,
    select: 'key,documentId,title,clientSlug,lastModifiedDateTime',
    ...(input.filterClause.length > 0 ? { filter: input.filterClause } : {}),
  };
  if (mode === 'keyword' || mode === 'hybrid') {
    body.search = input.query;
    body.queryType = 'simple';
    body.searchMode = 'all';
    body.highlight = 'body';
    body.highlightPreTag = '«';
    body.highlightPostTag = '»';
  } else {
    // 'vector' mode — explicit "no text search" so Azure doesn't apply
    // its default `*` match.
    body.search = '*';
    body.queryType = 'simple';
  }
  if (mode === 'vector' || mode === 'hybrid') {
    body.vectorQueries = [
      {
        kind: 'vector',
        vector: input.vector,
        fields: 'embedding',
        k: Math.max(top, 50),
      },
    ];
  }
  if (input.semantic === true) {
    body.queryType = 'semantic';
    body.semanticConfiguration = SEMANTIC_CONFIG_NAME;
    body.captions = 'extractive';
  }

  const res = await f(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'api-key': input.queryKey,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Azure AI Search HTTP ${res.status}: ${text}`);
  }
  const payload = (await res.json()) as {
    value?: Array<{
      key?: string;
      documentId?: string;
      title?: string;
      clientSlug?: string;
      lastModifiedDateTime?: string;
      '@search.score'?: number;
      '@search.rerankerScore'?: number;
      '@search.highlights'?: { body?: string[] };
      '@search.captions'?: Array<{ text?: string; highlights?: string }>;
    }>;
    '@odata.count'?: number;
  };

  const hits: CorpusHit[] = (payload.value ?? []).map((row) => {
    // Prefer semantic-extractive captions (with highlights) over body
    // highlights; fall back to body highlights; finally fall back to ''.
    const semanticHL = row['@search.captions']?.[0]?.highlights;
    const semanticText = row['@search.captions']?.[0]?.text;
    const snippet =
      semanticHL ??
      semanticText ??
      row['@search.highlights']?.body?.join(' … ') ??
      '';
    return {
      key: row.key ?? '',
      documentId: row.documentId ?? '',
      title: row.title ?? '(untitled)',
      clientSlug: row.clientSlug ?? '',
      snippet,
      // Prefer reranker score when present; it's on a different scale
      // (0–4) but more meaningful than BM25/vector raw scores.
      score: row['@search.rerankerScore'] ?? row['@search.score'] ?? 0,
      lastModifiedDateTime: row.lastModifiedDateTime ?? '',
    };
  });

  return {
    hits,
    totalCount: payload['@odata.count'] ?? null,
  };
}
